import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { filterSearchResults, SearchResult } from "../data/searchIndex";

type NavbarSearchProps = {
    variant?: "desktop" | "drawer";
    onNavigate?: () => void;
    onExpandedChange?: (expanded: boolean) => void;
    mobileGrowRef?: React.RefObject<HTMLDivElement | null>;
};

const MOBILE_NAV_MQ = "(max-width: 767px)";

const useMobileNav = () => {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia(MOBILE_NAV_MQ).matches,
    );

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_NAV_MQ);
        const sync = () => setIsMobile(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    return isMobile;
};

export const NavbarSearch = ({
    variant = "desktop",
    onNavigate,
    onExpandedChange,
    mobileGrowRef,
}: NavbarSearchProps) => {
    const navigate = useNavigate();
    const isMobileNav = useMobileNav();
    const containerRef = useRef<HTMLDivElement>(null);
    const growSlotRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isExpanded, setIsExpanded] = useState(variant === "drawer");
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const results = filterSearchResults(query);
    const isInlineMobile = variant === "desktop" && isMobileNav;
    const placeholder = isInlineMobile ? "Search..." : "Search solutions & software...";
    const useMobileGrow = isInlineMobile && Boolean(mobileGrowRef);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    useEffect(() => {
        onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const inTrigger = containerRef.current?.contains(target);
            const inGrow = growSlotRef.current?.contains(target) || mobileGrowRef?.current?.contains(target);

            if (inTrigger || inGrow) {
                return;
            }

            setIsOpen(false);
            if (isInlineMobile) {
                setQuery("");
                setActiveIndex(0);
                setIsExpanded(false);
            } else if (!query.trim() && variant === "desktop") {
                setIsExpanded(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [query, variant, isInlineMobile, mobileGrowRef]);

    useEffect(() => {
        if (isExpanded && useMobileGrow) {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [isExpanded, useMobileGrow]);

    const resetSearch = () => {
        setQuery("");
        setIsOpen(false);
        setActiveIndex(0);
        if (variant === "desktop") {
            setIsExpanded(false);
        }
    };

    const navigateToResult = (result: SearchResult) => {
        navigate(result.path);
        resetSearch();
        onNavigate?.();
    };

    const handleExpand = () => {
        setIsExpanded(true);
        if (!useMobileGrow) {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    };

    const handleCollapse = () => {
        resetSearch();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            event.preventDefault();
            handleCollapse();
            return;
        }

        if (!isOpen || results.length === 0) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((prev) => (prev + 1) % results.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (event.key === "Enter") {
            event.preventDefault();
            navigateToResult(results[activeIndex]);
        }
    };

    const handleChange = (value: string) => {
        setQuery(value);
        setIsOpen(value.trim().length > 0);
    };

    const searchField = (
        <div className="navbar-search__field-wrap">
            <SearchOutlined className="navbar-search__field-icon" />
            <input
                ref={inputRef}
                type="text"
                className="navbar-search__input"
                placeholder={placeholder}
                value={query}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => query.trim() && setIsOpen(true)}
                onKeyDown={handleKeyDown}
                aria-label="Search"
                aria-expanded={isOpen}
                aria-controls="navbar-search-results"
                aria-activedescendant={
                    isOpen && results.length > 0 ? `navbar-search-result-${activeIndex}` : undefined
                }
            />
            <button
                type="button"
                className="navbar-search__close-btn"
                aria-label="Close search"
                onClick={handleCollapse}
            >
                <CloseOutlined />
            </button>
        </div>
    );

    const searchDropdown =
        isOpen && isExpanded ? (
            <div id="navbar-search-results" className="navbar-search__dropdown" role="listbox">
                {results.length > 0 ? (
                    results.map((result, index) => (
                        <button
                            key={result.path}
                            id={`navbar-search-result-${index}`}
                            type="button"
                            role="option"
                            aria-selected={index === activeIndex}
                            className={`navbar-search__result ${index === activeIndex ? "navbar-search__result--active" : ""}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => navigateToResult(result)}
                        >
                            <span className="navbar-search__result-title">{result.title}</span>
                            <span className="navbar-search__result-category">{result.category}</span>
                        </button>
                    ))
                ) : (
                    <div className="navbar-search__empty">No results found</div>
                )}
            </div>
        ) : null;

    const mobileGrowField =
        useMobileGrow && isExpanded && mobileGrowRef?.current
            ? createPortal(
                  <div
                      ref={growSlotRef}
                      className="navbar-search navbar-search--inline-mobile navbar-search--expanded navbar-search--grow-slot"
                  >
                      {searchField}
                      {searchDropdown}
                  </div>,
                  mobileGrowRef.current,
              )
            : null;

    return (
        <>
            <div
                ref={containerRef}
                className={`navbar-search navbar-search--${variant} ${isExpanded ? "navbar-search--expanded" : ""} ${isInlineMobile ? "navbar-search--inline-mobile" : ""}`}
            >
                {!isExpanded ? (
                    <button
                        type="button"
                        className="navbar-search__icon-btn"
                        aria-label="Open search"
                        onClick={handleExpand}
                    >
                        <SearchOutlined />
                    </button>
                ) : (
                    !useMobileGrow && (
                        <>
                            {searchField}
                            {searchDropdown}
                        </>
                    )
                )}
            </div>
            {mobileGrowField}
        </>
    );
};
